import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { DOCUMENT, NgClass } from '@angular/common';
import { forkJoin, Observable, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LucideAngularModule, Layers, X, ChevronLeft, ChevronRight, Check, FolderOpen, CircleQuestionMark } from 'lucide-angular';
import { ProjectStepperService } from '@shared';
import { ProjectService, SettingsService } from '@admin';
import { StepDetectionScopeComponent } from './steps/step-detection-scope/step-detection-scope.component';
import { StepUploadPlanComponent }     from './steps/step-upload-plan/step-upload-plan.component';
import { StepAssignFloorsComponent }   from './steps/step-assign-floors/step-assign-floors.component';
import { StepResultsComponent }        from './steps/step-results/step-results.component';
import { StepperTourComponent }        from './stepper-tour/stepper-tour.component';
import { ButtonComponent }             from '../ui/button/button.component';
import { LoadingSpinnerComponent, ToastService } from '@shared';
import { ConfirmModalService }         from '@shared';
import { ConfirmModalComponent }       from '../ui/confirm-modal/confirm-modal.component';
import { PricingDrawerRoom, PricingDrawerSaveRequest } from '@core/models/project.models';
import { computeRoomMetrics, parseScaleString } from '@core/utils/geometry.utils';
import { CATEGORY } from '@core';
import { LanguageService } from '@core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

/**
 * Orchestrates the multi-step project detection workflow.
 */
@Component({
  selector: 'app-project-stepper',
  imports: [
    NgClass,
    LucideAngularModule,
    ButtonComponent,
    StepDetectionScopeComponent,
    StepUploadPlanComponent,
    StepAssignFloorsComponent,
    StepResultsComponent,
    LoadingSpinnerComponent,
    ConfirmModalComponent,
    TranslatePipe,
    StepperTourComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './project-stepper.component.html',
  styleUrl:    './project-stepper.component.css',
})
export class ProjectStepperComponent {
  /** Shared workflow state and API actions for the stepper. */
  readonly stepper = inject(ProjectStepperService);
  /** Project API client used for pricing drawer persistence. */
  private readonly projectService = inject(ProjectService);
  /** Settings state used to resolve selected materials and fixtures. */
  private readonly settingsService = inject(SettingsService);
  /** Toast notifications for save and workflow errors. */
  private readonly toast        = inject(ToastService);
  /** Confirmation modal used for pending scale-change prompts. */
  private readonly confirmModal = inject(ConfirmModalService);
  private readonly translate    = inject(TranslateService);
  private readonly lang         = inject(LanguageService);
  private readonly document     = inject(DOCUMENT);

  /** Locks the underlying page while the full-screen stepper is open. */
  private readonly bodyScrollLock = effect((onCleanup) => {
    if (!this.stepper.isOpen()) return;

    const win = this.document.defaultView;
    const body = this.document.body;
    const html = this.document.documentElement;
    if (!win || !body || !html) return;

    const scrollY = win.scrollY;
    const previousBodyStyle = {
      height: body.style.height,
      left: body.style.left,
      overflow: body.style.overflow,
      position: body.style.position,
      right: body.style.right,
      top: body.style.top,
      width: body.style.width,
    };
    const previousHtmlStyle = {
      height: html.style.height,
      overflow: html.style.overflow,
    };

    html.style.height = '100%';
    html.style.overflow = 'hidden';
    body.style.height = '100%';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    onCleanup(() => {
      html.style.height = previousHtmlStyle.height;
      html.style.overflow = previousHtmlStyle.overflow;
      body.style.height = previousBodyStyle.height;
      body.style.left = previousBodyStyle.left;
      body.style.overflow = previousBodyStyle.overflow;
      body.style.position = previousBodyStyle.position;
      body.style.right = previousBodyStyle.right;
      body.style.top = previousBodyStyle.top;
      body.style.width = previousBodyStyle.width;
      win.scrollTo(0, scrollY);
    });
  });

  /** Whether pricing drawer values are currently being saved before closing. */
  readonly pricingDrawerSaving = signal(false);

  /** Template icon for the stepper header. */
  readonly LayersIcon       = Layers;
  /** Template icon for close actions. */
  readonly XIcon            = X;
  /** Template icon for back navigation. */
  readonly ChevronLeftIcon  = ChevronLeft;
  /** Template icon for continue navigation. */
  readonly ChevronRightIcon = ChevronRight;
  /** Template icon for completion states. */
  readonly CheckIcon        = Check;
  /** Template icon for empty project/document states. */
  readonly FolderOpenIcon   = FolderOpen;
  /** Template icon for the replay-tour action. */
  readonly CircleQuestionMarkIcon = CircleQuestionMark;

  /** Primary action label for the current step. */
  readonly continueLabel = computed(() => {
    this.lang.currentLang();
    const labels: Record<number, string> = {
      1: 'PROJECT_STEPPER.NAV.CONTINUE_UPLOAD',
      2: 'PROJECT_STEPPER.NAV.CONTINUE_ASSIGN',
      3: 'PROJECT_STEPPER.NAV.CONTINUE_RESULTS',
      4: 'PROJECT_STEPPER.NAV.FINISH',
    };
    return this.translate.instant(labels[this.stepper.currentStep()] ?? 'PROJECT_STEPPER.NAV.CONTINUE');
  });

  /** Tooltip explaining what the primary action will do or why it is disabled. */
  readonly continueTooltip = computed(() => {
    this.lang.currentLang();
    if (this.pricingDrawerSaving()) return this.translate.instant('PROJECT_STEPPER.NAV.SAVING_PRICING');
    if (this.canContinue()) {
      return this.continueLabel();
    }

    switch (this.stepper.currentStep()) {
      case 1: return this.translate.instant('PROJECT_STEPPER.NAV.SELECT_CATEGORIES_TOOLTIP', {
        max: this.stepper.maxDetectionScopeCategories,
      });
      case 2: return this.translate.instant('PROJECT_STEPPER.NAV.UPLOAD_TOOLTIP');
      case 3: {
        const hasAssignment = this.stepper.floorAssignments().length > 0;
        const hasScale      = this.stepper.selectedScale() !== null;
        if (!hasAssignment && !hasScale) return this.translate.instant('PROJECT_STEPPER.NAV.ASSIGN_AND_SCALE_TOOLTIP');
        if (!hasAssignment) return this.translate.instant('PROJECT_STEPPER.NAV.ASSIGN_TOOLTIP');
        return this.translate.instant('PROJECT_STEPPER.NAV.SCALE_TOOLTIP');
      }
      default: return '';
    }
  });

  /** Whether the current step has enough required data to continue. */
  readonly canContinue = computed(() => {
    switch (this.stepper.currentStep()) {
      case 1:
        return (
          this.stepper.selectedCategories().length > 0 &&
          this.stepper.selectedCategories().length <= this.stepper.maxDetectionScopeCategories
        );
      case 2: return this.stepper.uploadComplete() || this.stepper.existingDocuments().length > 0;
      case 3: return this.stepper.floorAssignments().length > 0 && this.stepper.selectedScale() !== null;
      default: return true;
    }
  });

  /**
   * Closes the workflow after guarding unsaved result edits.
   */
  onClose(): void {
    if (this.stepper.step4EditDirty() && !window.confirm(this.translate.instant('PROJECT_STEPPER.CONFIRM.CLOSE_DIRTY'))) return;
    this.stepper.close();
  }

  /**
   * Moves to the previous step after guarding unsaved result edits.
   */
  onBack(): void {
    if (this.stepper.step4EditDirty() && !window.confirm(this.translate.instant('PROJECT_STEPPER.CONFIRM.BACK_DIRTY'))) return;
    this.stepper.prev();
  }

  /**
   * Performs the current step's continue action.
   */
  onContinue(): void {
    if (this.stepper.currentStep() === 1) {
      if (this.stepper.detectionScopeSaving()) return;
      this.stepper.saveDetectionScope().subscribe({
        next:  () => this.stepper.next(),
        error: () => this.toast.error(
          this.translate.instant('PROJECT_STEPPER.TOAST.SAVE_FAILED_TITLE'),
          this.translate.instant('PROJECT_STEPPER.TOAST.SAVE_SCOPE_FAILED_MESSAGE'),
        ),
      });
      return;
    }

    if (this.stepper.currentStep() === 3) {
      if (this.stepper.pendingScaleChange()) {
        this.confirmModal.confirm({
          title:        this.translate.instant('PROJECT_STEPPER.CONFIRM.SCALE_TITLE'),
          message:      this.translate.instant('PROJECT_STEPPER.CONFIRM.SCALE_MESSAGE'),
          confirmLabel: this.translate.instant('PROJECT_STEPPER.CONFIRM.CONTINUE_ANYWAY'),
          cancelLabel:  this.translate.instant('PROJECT_STEPPER.CONFIRM.GO_BACK'),
        }).then(confirmed => {
          if (!confirmed) return;
          this.stepper.pendingScaleChange.set(false);
          this.stepper.detectionTriggered.set(false);
          this.stepper.categoryResults.set([]);
          this.stepper.resetPreProcessState();
          this.stepper.next();
        });
        return;
      }
      this.stepper.detectionTriggered.set(false);
      this.stepper.categoryResults.set([]);
      this.stepper.resetPreProcessState();
      this.stepper.next();
      return;
    }

    if (this.stepper.currentStep() < this.stepper.steps.length) {
      if (this.stepper.currentStep() === 4) {
        this.savePricingAndContinue();
        return;
      }
      this.stepper.next();
      return;
    }

    this.stepper.close();
  }

  /**
   * Saves pricing drawer changes before finishing the stepper.
   */
  private savePricingAndContinue(): void {
    const projectId = this.stepper.project()?.projectId;
    if (!projectId) {
      this.stepper.close();
      return;
    }

    this.pricingDrawerSaving.set(true);
    this.savePricingDrawers(projectId)
      .pipe(finalize(() => this.pricingDrawerSaving.set(false)))
      .subscribe({
        next: () => this.stepper.close(),
        error: () => this.toast.error(
          this.translate.instant('PROJECT_STEPPER.TOAST.PRICING_SAVE_FAILED_TITLE'),
          this.translate.instant('PROJECT_STEPPER.TOAST.PRICING_SAVE_FAILED_MESSAGE'),
        ),
      });
  }

  /**
   * Saves all supported pricing drawer payloads for the project.
   *
   * @param projectId Project id that owns the pricing data.
   * @returns Observable that completes after all save requests finish.
   */
  private savePricingDrawers(projectId: number): Observable<unknown> {
    const flooringPayloads = this.buildFlooringPricingPayloads();
    const plumbingPayloads = this.buildPlumbingPricingPayloads();
    const requests = [
      ...flooringPayloads.map(payload => this.projectService.savePricingDrawer(projectId, payload)),
      ...plumbingPayloads.map(payload => this.projectService.savePlumbingPricingDrawer(projectId, payload)),
    ];
    if (!requests.length) return of([]);
    return forkJoin(requests);
  }

  /**
   * Builds save payloads for completed flooring results.
   */
  private buildFlooringPricingPayloads(): PricingDrawerSaveRequest[] {
    const category = this.stepper.categoryResults()
      .find(result => result.categoryId === CATEGORY.FLOORING.id && result.status === 'complete');
    if (!category) return [];

    return category.floors
      .map((floor): PricingDrawerSaveRequest => {
        const scale = this.stepper.scaleForFloor(floor.floorName);
        return {
          categoryId: CATEGORY.FLOORING.numericId,
          floorName: floor.floorName,
          rooms: floor.rooms
            .map((room): PricingDrawerRoom | null => {
              const materialName = this.stepper.getRoomMaterial(CATEGORY.FLOORING.id, floor.floorName, room.name);
              const material = this.findFlooringMaterial(materialName);
              if (!material) return null;

              const quantity = scale
                ? Math.ceil(computeRoomMetrics(room.points, parseScaleString(scale)).areaSqft)
                : Math.ceil(room.area ?? 0);

              return {
                roomName: room.name,
                materialId: material.id,
                materialName: material.name,
                unit: material.unit,
                unitPrice: material.unitPrice,
                quantity,
              };
            })
            .filter((room): room is PricingDrawerRoom => room !== null),
        };
      })
      .filter(payload => payload.rooms.length > 0);
  }

  /**
   * Resolves a selected flooring material name to an active settings material.
   *
   * @param materialName Material display name selected in the UI.
   */
  private findFlooringMaterial(materialName: string) {
    const selected = materialName.trim().toLowerCase();
    if (!selected) return null;
    return this.settingsService.settings().scopes
      .find(scope => scope.categoryId === CATEGORY.FLOORING.numericId)
      ?.materials
      .find(material => material.active && material.name.trim().toLowerCase() === selected) ?? null;
  }

  /**
   * Builds save payloads for completed plumbing fixture results.
   */
  private buildPlumbingPricingPayloads(): PricingDrawerSaveRequest[] {
    const category = this.stepper.categoryResults()
      .find(result => result.categoryId === CATEGORY.FIXTURES.id && result.status === 'complete');
    if (!category) return [];

    return category.floors
      .map((floor): PricingDrawerSaveRequest => ({
        categoryId: CATEGORY.FIXTURES.numericId,
        floorName: floor.floorName,
        rooms: floor.rooms
          .map((room): PricingDrawerRoom | null => {
            const fixtureName = this.effectivePlumbingFixtureName(floor.floorName, room.name, room.roomName);
            const fixture = this.findPlumbingFixture(fixtureName);
            if (!fixture) return null;
            const savedRate = this.stepper.getRoomMaterialRate(CATEGORY.FIXTURES.id, floor.floorName, room.name);
            const savedQty = this.stepper.getRoomMaterialQuantity(CATEGORY.FIXTURES.id, floor.floorName, room.name);

            return {
              roomName: room.name,
              materialId: fixture.id,
              materialName: fixture.name,
              unit: fixture.unit || 'ea',
              unitPrice: savedRate ?? fixture.unitPrice,
              quantity: savedQty ?? 1,
            };
          })
          .filter((room): room is PricingDrawerRoom => room !== null),
      }))
      .filter(payload => payload.rooms.length > 0);
  }

  /**
   * Resolves the effective plumbing fixture name, including bathroom sink fallback.
   *
   * @param floorName Floor that owns the fixture.
   * @param fixtureName Detected fixture key.
   * @param roomName Optional room name for fixture context.
   */
  private effectivePlumbingFixtureName(floorName: string, fixtureName: string, roomName?: string): string {
    const selected = this.stepper.getRoomMaterial(CATEGORY.FIXTURES.id, floorName, fixtureName) || fixtureName;
    if (selected.trim().toLowerCase() === 'sink' && (roomName ?? fixtureName).toLowerCase().includes('bath')) {
      return 'Bathroom Sink';
    }
    return selected;
  }

  /**
   * Resolves a plumbing fixture name to an active settings fixture.
   *
   * @param fixtureName Fixture display name selected in the UI.
   */
  private findPlumbingFixture(fixtureName: string) {
    const selected = fixtureName.trim().toLowerCase();
    if (!selected) return null;
    return this.settingsService.settings().scopes
      .find(scope => scope.categoryId === CATEGORY.FIXTURES.numericId)
      ?.fixtures
      ?.find(fixture => fixture.active && fixture.name.trim().toLowerCase() === selected) ?? null;
  }
}
